import { Button, Grid, useDisclosure } from "@chakra-ui/react";
import { Card, Spinner } from "@components/ui-kit";
import { NotFoundCreditAccount } from "@components/widgets/ErrorPage";
import { ModalTransferCredit } from "@components/widgets/Modals/ModalTransferCredit";
import { Trans, t } from "@lingui/macro";
import { useGetCreditAccountQuery } from "@src/__generated__/graphql";
import { Item } from "@src/components/modules/invoices/Detail/components/common/Item";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { currency } from "@src/utils/formatters";
import { getQueryParam } from "@src/utils/routing";
import cx from "classnames";
import { map } from "lodash";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { createContext, useContext, useState } from "react";
import { ModalEditCreditAccount } from "../form/ModalEditCreditAccount";
import { CardHeader, CardIcon } from "./common";
import { BrandCreditAccountModel } from "./models";
import { StoreCreditAccount } from "./StoreCreditAccount";
import { TableCredits } from "./table/TableCredits";
import { TableDebits } from "./table/TableDebits";

export const PageCreditAccountCtx = createContext({} as StoreCreditAccount);

/**
 * encapsulate initial data fetch & refetch
 */
function useData(store: StoreCreditAccount) {
  const { query } = useRouter();

  const id = getQueryParam("id", query);

  const { data, loading, refetch } = useGetCreditAccountQuery({
    variables: { id },
    skip: !id,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (data.brandCreditAccount) {
        store.creditAccount = new BrandCreditAccountModel(
          data.brandCreditAccount,
        );
      }
    },
    ...onError(),
  });

  store.refetch = refetch;

  return { data, loading };
}

const PageCreditAccountInner = observer(function PageCreditAccountInner() {
  const store = useContext(PageCreditAccountCtx);
  const { creditAccount } = store;

  const editCreditAccountModal = useDisclosure();
  const modalTransferCredit = useDisclosure();

  const mergedBudgetCategories = [
    ...(creditAccount?.ourWorkBudgetCategories || []),
    ...(creditAccount?.expenseBudgetCategories || []),
  ];

  if (!store.creditAccount?.id) {
    return null;
  }

  return (
    <div>
      <div className="flex">
        <Grid
          rowGap="2"
          columnGap="4"
          templateColumns={{
            base: "1fr",
            md: "66% auto",
          }}
          w="full"
        >
          <Card>
            <div className="px-8 py-6">
              <CardHeader
                title={creditAccount?.brand.name || ""}
                icon={
                  <div
                    className="w-16 h-16 rounded-full overflow-hidden bg-inkLightest bg-cover"
                    style={{
                      background:
                        creditAccount?.brand.image?.urls.thumbnail || undefined,
                    }}
                  />
                }
                additionalInfo={
                  <span>
                    <span className="font-semibold">
                      <Trans>Budget category:</Trans>
                    </span>
                    <span>
                      {" "}
                      {mergedBudgetCategories.length !== 0 &&
                        map(
                          mergedBudgetCategories,
                          (category, index) =>
                            `${category?.name}${
                              index !== mergedBudgetCategories.length - 1
                                ? ","
                                : ""
                            } `,
                        )}
                    </span>
                    <Item label={t`Team`}>
                      {store.creditAccount?.team.name}
                    </Item>
                  </span>
                }
                button={
                  <Button
                    leftIcon={<Icon name="pencil-01" />}
                    {...editCreditAccountModal.getButtonProps()}
                    variant="outline"
                  >
                    <Trans>Edit details</Trans>
                  </Button>
                }
              />
            </div>

            {!!creditAccount?.id && (
              <ModalEditCreditAccount
                {...editCreditAccountModal.getDisclosureProps()}
                {...editCreditAccountModal}
                creditAccount={creditAccount}
              />
            )}
          </Card>

          <Card>
            <div className="px-8 py-6">
              <CardHeader
                title={t`Remaining credit`}
                icon={<CardIcon />}
                additionalInfo={
                  <span className="font-semibold">
                    {currency.formatByWorkspace(
                      creditAccount?.stats.remaining_credit || 0,
                    )}
                  </span>
                }
                button={
                  <Button
                    disabled={!creditAccount?.id}
                    onClick={modalTransferCredit.onOpen}
                  >{t`Transfer credit`}</Button>
                }
              />
              {!!creditAccount?.id && (
                <ModalTransferCredit
                  {...modalTransferCredit}
                  brandCreditAccountId={creditAccount.id}
                  onTransfer={store.refetch}
                />
              )}
            </div>
          </Card>
        </Grid>
      </div>

      <div className="pt-10">
        <TableCredits />
        <div className="pt-10 w-full flex justify-end items-center">
          <span className="mr-4 text-inkLightest">
            <Trans>Total credit</Trans>
          </span>
          <span
            className={cx(
              {
                "text-red": Boolean(
                  Math.sign(store.creditAccount?.stats.total_credit || 0) ===
                    -1,
                ),
              },
              "text-4xl",
            )}
          >
            {currency.formatByWorkspace(
              store.creditAccount?.stats.total_credit || 0,
            )}
          </span>
        </div>

        <div className="pt-16">
          <TableDebits />
          <div className="pt-10 w-full flex justify-end items-center">
            <span className="mr-4 text-inkLightest">
              <Trans>Total debits</Trans>
            </span>
            <span
              className={cx(
                {
                  "text-red": Boolean(
                    Math.sign(store.creditAccount?.stats.total_debit || 0) ===
                      -1,
                  ),
                },
                "text-4xl",
              )}
            >
              {currency.formatByWorkspace(
                store.creditAccount?.stats.total_debit || 0,
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

export const PageCreditAccount = observer(function PageCreditAccount() {
  const [store] = useState(() => {
    return new StoreCreditAccount();
  });
  const { data, loading } = useData(store);

  if (loading) {
    return <Spinner absolute />;
  }

  if (!data?.brandCreditAccount) {
    return <NotFoundCreditAccount />;
  }

  return (
    <PageCreditAccountCtx.Provider value={store}>
      <div className="flex justify-center items-center">
        <div className="w-full">
          <PageCreditAccountInner />
        </div>
      </div>
    </PageCreditAccountCtx.Provider>
  );
});
